import './AccessChangesTable.scss';

import { AccessChangesExportButton } from './AccessChangesExportButton';
import { CheckboxChangeEvent } from 'antd/es/checkbox/Checkbox';
import { createAsyncSelectors } from '@lib/selectors';
import { DataSecurityViewQueryParams } from '@security/DataSecurityView';
import { DateRangePickerParams } from '@ui/DateRangePicker';
import { getColumns } from './AccessChangesTableColumns';
import { SonarCard } from '@ui/general';
import { useNavigate } from 'react-router';

import { AccessChangeType, AccessChangeTypeTag } from './AccessChangeTypeTag';
import { Checkbox, Select } from 'antd';
import { COPY_GENERIC, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { coreActions, DataSecurityAlert, FeatureFlag } from '@sonar-software/sonar-core';
import { DynamicQueryOperator, PaginationDirection } from '@sonar-software/sonar-ts-types';
import { getPastDate, queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import { PaginatedTable, PaginatedTableQueryParams } from '@ui/tables/PaginatedTable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useAction,
	useAsyncState,
	useCurrentUser,
	useFeatureFlag,
	useOnMount,
	usePrevious,
	useQueryParams
} from '@hooks';

const getRowKey = (result: DataSecurityAlert) => result.id;

const dataSecurityAlertsSelectors = createAsyncSelectors((state) => state.core.dataSecurity.alerts.get);

const changeTypeOptions = Object.values(AccessChangeType)
	.filter((value) => Number.isInteger(value))
	.map((changeType) => ({
		label: <AccessChangeTypeTag changeType={ changeType as AccessChangeType } />,
		value: changeType
	}));

export type AccessChangesTableQueryParams = PaginatedTableQueryParams & DateRangePickerParams & {
	appGroup?: number;
	applicationId?: number;
	changeType?: AccessChangeType;
	showHidden?: boolean;
};

const convertToDql = (params: AccessChangesTableQueryParams) => queryParamsToDynamicQueryLanguageParams(params, {
	defaultOperator: DynamicQueryOperator.Equals,
	filterParams: {
		appGroup: {
			name: 'stakeholderGroupId',
			value: (value) => (value === null ? undefined : value)
		},
		applicationId: true,
		changeType: { name: 'monitorType' },
		showHidden: {
			name: 'hidden',
			value: (value) => (value ? undefined : false)
		},
		start: {
			name: 'detectedDate',
			rangeEnd: 'end'
		}
	}
});

export interface AccessChangesTableProps {
	applicationId?: number;
	viewParams: DataSecurityViewQueryParams;
}

export const AccessChangesTable: React.FC<AccessChangesTableProps> = ({ applicationId, viewParams }) => {
	const navigate = useNavigate();
	const allowOverview = useFeatureFlag(FeatureFlag.DataSecurityDashboard);
	const getDataSecurityAlerts = useAction(coreActions.requestGetCoreDataSecurityAlerts);
	const hideDataSecurityAlert = useAction(coreActions.requestPostCoreDataSecurityAlert);
	const [ firstLoad, setFirstLoad ] = useState(true);
	const [ dateChanged, setDateChanged ] = useState(false);
	const previousApplicationId = usePrevious(applicationId);
	const applicationIdChanged = applicationId !== previousApplicationId;

	const { data, isRequested } = useAsyncState(dataSecurityAlertsSelectors);
	const { isReadOnly } = useCurrentUser();

	const { queryParams, updateParams } = useQueryParams<AccessChangesTableQueryParams>({
		defaultParams: {
			applicationId,
			count: 10,
			showHidden: false,
			sortBy: 'detectedDate',
			sortDirection: 1,
			start: getPastDate(7)
		},
		onUpdate: (_newParams, _prev, combined) => {
			getDataSecurityAlerts(
				convertToDql(combined)
			);
		}
	});

	useOnMount(() => {
		setFirstLoad(false);

		getDataSecurityAlerts(
			convertToDql(queryParams)
		);
	});

	useEffect(() => {
		if (!applicationId && viewParams.appGroup !== queryParams?.appGroup) {
			updateParams({ appGroup: viewParams.appGroup });
		}
	}, [ applicationId, queryParams?.appGroup, updateParams, viewParams.appGroup ]);

	useEffect(() => {
		if (viewParams.dateRange.end !== queryParams?.end || viewParams.dateRange.start !== queryParams?.start) {
			setDateChanged(true);
			const { start, end } = viewParams.dateRange;

			updateParams({
				end,
				start
			});
		} else if (dateChanged) {
			setDateChanged(false);
		}
	}, [ dateChanged, viewParams, queryParams?.end, queryParams?.start, updateParams, queryParams?.appGroup ]);

	const handleShowHidden = useCallback((e: CheckboxChangeEvent) => {
		updateParams({ showHidden: e.target.checked });
	}, [ updateParams ]);

	const handleTypeSelect = useCallback((changeType: AccessChangeType) => {
		updateParams({ changeType });
	}, [ updateParams ]);

	const setHidden = useCallback((monitorAlertId: number, hide: boolean) => {
		const pageEmpty = queryParams.key && hide && data?.results.length === 1;
		const onFirstPage = queryParams.key && data?.nextKey && !data?.prevKey;

		if (pageEmpty || onFirstPage) {
			updateParams({
				...queryParams,
				key: '',
				paginationDirection: PaginationDirection.Forward
			});
		}

		hideDataSecurityAlert({
			hide,
			monitorAlertId
		});
	}, [ data?.nextKey, data?.prevKey, data?.results.length, hideDataSecurityAlert, queryParams, updateParams ]);

	const columns = useMemo(
		() => getColumns(!allowOverview || Boolean(applicationId), isReadOnly, navigate, setHidden),
		[ allowOverview, applicationId, isReadOnly, navigate, setHidden ]
	);

	return (
		<SonarCard
			className='access-changes-table'
			extra={ <AccessChangesExportButton queryParams={ convertToDql(queryParams) } /> }
			loading={ firstLoad || dateChanged || applicationIdChanged }
			noSpacing
			title={ COPY_SPECIFIC.ACCESS_CHANGES_DETECTED }
			titleClassName='h2'
			useSpinnerLoading>
			<div className='table-filters'>
				<Select
					allowClear
					className='change-type-select'
					onChange={ handleTypeSelect }
					options={ changeTypeOptions }
					placeholder={ COPY_SPECIFIC.TYPE } />
				<Checkbox
					className='show-hidden'
					onChange={ handleShowHidden }>
					{ COPY_GENERIC.SHOW_HIDDEN }
				</Checkbox>
			</div>
			<div className='table-container'>
				<PaginatedTable
					autohidePagination
					columns={ columns }
					defaultPageSize={ 10 }
					id='access-changes-table'
					loading={ isRequested }
					onChange={ updateParams }
					resource={ data }
					rowClassName={ (record) => (record.hidden ? 'hidden' : '') }
					rowKey={ getRowKey }
					showSizeChanger={ false }
					sortBy={ queryParams.sortBy }
					sortDirection={ queryParams.sortDirection } />
			</div>
		</SonarCard>
	);
};
